import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';

const OnboardingStep4 = ({ onComplete }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [hasDisciple, setHasDisciple] = useState(false);
  const [firstDiscipleId, setFirstDiscipleId] = useState(null);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    // Check if user has disciples
    if (currentUser?.discipleIds && currentUser.discipleIds.length > 0) {
      setHasDisciple(true);
      setFirstDiscipleId(currentUser.discipleIds[0]);
    }
  }, [currentUser]);

  const handleSendMessage = () => {
    if (firstDiscipleId) {
      navigate(`/send-message?discipleId=${firstDiscipleId}`);
    } else {
      alert('Please add a disciple first');
    }
  };

  const handleCompleteOnboarding = async () => {
    if (!currentUser?.uid) {
      alert('Please log in first');
      return;
    }

    setCompleting(true);
    try {
      // Mark onboarding as complete
      await updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingComplete: true,
        onboardingStep: 4,
      });

      // Call parent completion handler
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to complete onboarding. Please try again.');
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0076BE] to-[#5ABA47]">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#0076BE] mb-2">
              Send Your First Message
            </h2>
            <p className="text-gray-600">Step 4 of 4</p>
          </div>

          <p className="text-gray-700 mb-6 text-center">
            You're almost done! Send your first message to connect with your disciple. You can include:
          </p>

          <ul className="space-y-3 mb-8">
            <li className="flex items-start">
              <span className="text-[#5ABA47] mr-3 text-xl">•</span>
              <span className="text-gray-700">A personal message</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#5ABA47] mr-3 text-xl">•</span>
              <span className="text-gray-700">A Bible verse</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#5ABA47] mr-3 text-xl">•</span>
              <span className="text-gray-700">A reflection question</span>
            </li>
          </ul>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4">
            {hasDisciple ? (
              <>
                <button
                  onClick={handleSendMessage}
                  className="w-full px-6 py-3 bg-[#5ABA47] text-white font-semibold rounded-lg hover:bg-[#4a9d3a] transition-colors"
                >
                  Send First Message
                </button>
                <button
                  onClick={handleCompleteOnboarding}
                  disabled={completing}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {completing ? 'Completing...' : 'Complete Setup'}
                </button>
              </>
            ) : (
              <>
                <p className="text-center text-gray-600 mb-4">
                  Add a disciple first to send a message
                </p>
                <button
                  onClick={() => navigate('/add-disciple')}
                  className="w-full px-6 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#005f98] transition-colors mb-2"
                >
                  Add Disciple
                </button>
                <button
                  onClick={handleCompleteOnboarding}
                  disabled={completing}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {completing ? 'Completing...' : 'Complete Setup'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep4;
